import React, { useState, useEffect } from 'react';

// Utility function to get auth token
const getAuthToken = () => {
  return localStorage.getItem('authToken');
};

interface ExpiryAlertItem {
  id?: number;
  medicine_name?: string;
  batch_no?: string;
  expiry_date?: string;
  days_until_expiry?: number;
  quantity?: number;
  quantity_type?: string;
  storage_location?: string;
  [key: string]: any; // For any additional fields
}

const ExpiryAlerts: React.FC = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [expiryAlerts, setExpiryAlerts] = useState<ExpiryAlertItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [daysThreshold, setDaysThreshold] = useState(30);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Filter out already expired medicines (only show expiring-soon, not past expiry)
  const filterOutExpired = (items: ExpiryAlertItem[]): ExpiryAlertItem[] => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return items.filter((item) => {
      const days = item.days_until_expiry;
      if (days !== undefined && days !== null && days < 0) return false;
      const expStr = item.expiry_date;
      if (expStr) {
        try {
          const expDate = new Date(expStr);
          expDate.setHours(0, 0, 0, 0);
          if (expDate < today) return false;
        } catch {
          // keep if date parse fails
        }
      }
      return true;
    });
  };

  // Fetch expiry alerts
  const fetchExpiryAlerts = async () => {
    try {
      setIsLoading(true);
      setError('');
      
      const authToken = getAuthToken();
      
      if (!authToken) {
        setError('Authentication token not found. Please log in again.');
        setIsLoading(false);
        return;
      }
      
      const response = await fetch(
        `https://pharma-amd-0-1-42.onrender.com/pharma/api/inventory/expiry-alerts?days_threshold=${daysThreshold}`,
        {
          method: 'GET',
          mode: 'cors',
          headers: {
            'Content-Type': 'application/json',
            'ngrok-skip-browser-warning': 'true',
            'Authorization': `Bearer ${authToken}`
          }
        }
      );
      
      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.detail || errorJson.error || errorJson.message || errorMessage;
        } catch (e) {
          // If not JSON, use the text as is
          if (errorText) errorMessage = errorText;
        }
        throw new Error(errorMessage);
      }
      
      const data = await response.json();
      
      // Handle different response structures; exclude already expired medicines
      if (Array.isArray(data)) {
        setExpiryAlerts(filterOutExpired(data));
      } else if (data && Array.isArray(data.items)) {
        setExpiryAlerts(filterOutExpired(data.items));
      } else if (data && Array.isArray(data.alerts)) {
        setExpiryAlerts(filterOutExpired(data.alerts));
      } else if (data && Array.isArray(data.data)) {
        setExpiryAlerts(filterOutExpired(data.data));
      } else if (data && typeof data === 'object') {
        // If it's an object, try to extract array values
        const values = Object.values(data);
        const arrayValue = values.find(v => Array.isArray(v)) as ExpiryAlertItem[] | undefined;
        if (arrayValue) {
          setExpiryAlerts(filterOutExpired(arrayValue));
        } else {
          setExpiryAlerts(filterOutExpired([data]));
        }
      } else {
        setExpiryAlerts([]);
      }
    } catch (error: any) {
      console.error('Error fetching expiry alerts:', error);
      
      if (error.message.includes('401') || error.message.includes('Unauthorized')) {
        setError('Authentication failed. Please log in again.');
        // Don't auto-remove token - let user manually logout
      } else if (error.message.includes('403') || error.message.includes('Forbidden')) {
        setError('Access denied. You do not have permission to view expiry alerts.');
      } else if (error.message.includes('CORS') || error.message.includes('Failed to fetch')) {
        setError('Network error: Unable to connect to the API. Please check your connection and try again.');
      } else {
        setError(`Failed to load expiry alerts: ${error.message || 'Unknown error'}`);
      }
      
      setExpiryAlerts([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchExpiryAlerts();
  }, [daysThreshold]);

  // Format date for display
  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (e) {
      return dateString;
    }
  };

  // Get color based on days until expiry
  const getDaysColor = (days: number | undefined): string => {
    if (days === undefined || days === null) return '#6b7280';
    if (days < 0) return '#991b1b'; // Dark red for expired items
    if (days < 30) return '#ef4444'; // Red for less than 30 days
    if (days < 90) return '#f59e0b'; // Orange for less than 90 days
    if (days < 180) return '#eab308'; // Yellow for less than 180 days
    return '#10b981'; // Green for more than 180 days
  };

  return (
    <div style={{
      width: '100%',
      maxWidth: '100%',
      boxSizing: 'border-box'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between',
        alignItems: isMobile ? 'stretch' : 'center',
        marginBottom: '1.5rem',
        flexWrap: 'wrap',
        gap: isMobile ? '1rem' : '1rem'
      }}>
        <div style={{
          width: isMobile ? '100%' : 'auto'
        }}>
          <p style={{
            fontSize: isMobile ? '0.85rem' : '0.9rem',
            color: '#6b7280',
            margin: '0 0 0.5rem 0',
            textAlign: isMobile ? 'center' : 'left'
          }}>
            Medicines expiring within {daysThreshold} days
          </p>
        </div>
        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? '0.75rem' : '0.75rem',
          alignItems: isMobile ? 'stretch' : 'center',
          flexWrap: 'wrap',
          width: isMobile ? '100%' : 'auto'
        }}>
          <div style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: isMobile ? 'stretch' : 'center',
            gap: isMobile ? '0.5rem' : '0.5rem',
            width: isMobile ? '100%' : 'auto'
          }}>
            <label style={{
              fontSize: isMobile ? '0.8rem' : '0.9rem',
              color: '#374151',
              fontWeight: '500',
              textAlign: isMobile ? 'center' : 'left',
              width: isMobile ? '100%' : 'auto'
            }}>
              Days Threshold:
            </label>
            <input
              type="number"
              value={daysThreshold}
              onChange={(e) => {
                const value = parseInt(e.target.value) || 0;
                if (value >= 0) {
                  setDaysThreshold(value);
                }
              }}
              style={{
                padding: isMobile ? '0.75rem' : '0.5rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: isMobile ? '0.9rem' : '0.9rem',
                width: isMobile ? '100%' : '100px',
                maxWidth: isMobile ? '100%' : '100px',
                outline: 'none',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#3b82f6';
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#d1d5db';
                e.currentTarget.style.boxShadow = 'none';
              }}
            />
          </div>
          <button
            onClick={fetchExpiryAlerts}
            disabled={isLoading}
            style={{
              background: isLoading
                ? 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)'
                : 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              padding: isMobile ? '0.75rem 1rem' : '0.625rem 1.25rem',
              fontSize: isMobile ? '0.85rem' : '0.9rem',
              fontWeight: '600',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              width: isMobile ? '100%' : 'auto',
              boxSizing: 'border-box'
            }}
            onMouseEnter={(e) => {
              if (!isLoading && e.currentTarget) {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 4px 8px rgba(59, 130, 246, 0.3)';
              }
            }}
            onMouseLeave={(e) => {
              if (e.currentTarget) {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }
            }}
          >
            {isLoading ? (
              <>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ animation: 'spin 1s linear infinite' }}>
                  <line x1="12" y1="2" x2="12" y2="6"/>
                  <line x1="12" y1="18" x2="12" y2="22"/>
                  <line x1="4.93" y1="4.93" x2="7.76" y2="7.76"/>
                  <line x1="16.24" y1="16.24" x2="19.07" y2="19.07"/>
                  <line x1="2" y1="12" x2="6" y2="12"/>
                  <line x1="18" y1="12" x2="22" y2="12"/>
                  <line x1="4.93" y1="19.07" x2="7.76" y2="16.24"/>
                  <line x1="16.24" y1="7.76" x2="19.07" y2="4.93"/>
                </svg>
                Loading...
              </>
            ) : (
              <>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="23 4 23 10 17 10"/>
                  <polyline points="1 20 1 14 7 14"/>
                  <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
                </svg>
                Refresh
              </>
            )}
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div style={{
          padding: '1rem',
          background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
          border: '2px solid #ef4444',
          borderRadius: '8px',
          marginBottom: '1rem',
          color: '#991b1b'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem'
          }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            <span style={{ fontWeight: '600' }}>{error}</span>
          </div>
        </div>
      )}

      {/* Loading State */}
      {isLoading && !error && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '3rem',
          color: '#6b7280'
        }}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ animation: 'spin 1s linear infinite' }}>
              <line x1="12" y1="2" x2="12" y2="6"/>
              <line x1="12" y1="18" x2="12" y2="22"/>
              <line x1="4.93" y1="4.93" x2="7.76" y2="7.76"/>
              <line x1="16.24" y1="16.24" x2="19.07" y2="19.07"/>
              <line x1="2" y1="12" x2="6" y2="12"/>
              <line x1="18" y1="12" x2="22" y2="12"/>
              <line x1="4.93" y1="19.07" x2="7.76" y2="16.24"/>
              <line x1="16.24" y1="7.76" x2="19.07" y2="4.93"/>
            </svg>
            <span style={{ fontSize: '0.9rem', fontWeight: '500' }}>Loading expiry alerts...</span>
          </div>
        </div>
      )}

      {/* Cards */}
      {!isLoading && !error && (
        <>
          {expiryAlerts.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '3rem',
              color: '#6b7280'
            }}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ margin: '0 auto 1rem', opacity: 0.5 }}>
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              <p style={{ fontSize: '1rem', fontWeight: '500', margin: 0 }}>
                No expiry alerts found
              </p>
              <p style={{ fontSize: '0.875rem', margin: '0.5rem 0 0 0', opacity: 0.7 }}>
                No medicines are expiring within {daysThreshold} days.
              </p>
            </div>
          ) : (
            <div 
              className="expiry-alerts-scroll"
              style={{
                display: 'grid',
                gap: isMobile ? '0.75rem' : '1rem',
                maxHeight: isMobile ? '400px' : '400px',
                overflowY: 'auto',
                overflowX: 'hidden',
                scrollbarWidth: 'thin',
                scrollbarColor: '#ef4444 #f1f5f9',
                paddingRight: isMobile ? '0.25rem' : '0.5rem',
                width: '100%',
                boxSizing: 'border-box'
              }}>
              {expiryAlerts.map((item, index) => {
                const medicineName = item.medicine_name || 'N/A';
                const composition = item.medicine_composition || 'N/A';
                const expiryDate = item.expiry_date || null;
                const batchNo = item.batch_no || 'N/A';
                const daysToExpiry = item.days_to_expiry !== undefined && item.days_to_expiry !== null ? item.days_to_expiry : null;
                const isExpired = daysToExpiry !== null && daysToExpiry < 0;
                const daysColor = getDaysColor(typeof daysToExpiry === 'number' ? daysToExpiry : undefined);
                
                return (
                  <div 
                    key={item.id || index} 
                    style={{
                      background: 'white',
                      borderRadius: isMobile ? '10px' : '12px',
                      border: `1px solid ${daysColor}40`,
                      padding: isMobile ? '0.875rem' : '1rem',
                      boxShadow: `0 2px 8px ${daysColor}20`,
                      transition: 'all 0.3s ease',
                      position: 'relative',
                      width: '100%',
                      boxSizing: 'border-box'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = `0 4px 16px ${daysColor}30`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = `0 2px 8px ${daysColor}20`;
                    }}>
                    {/* Days Badge */}
                    {daysToExpiry !== null && (
                      <div style={{
                        position: isMobile ? 'static' : 'absolute',
                        top: isMobile ? 'auto' : '1rem',
                        right: isMobile ? 'auto' : '1rem',
                        background: daysColor,
                        color: 'white',
                        padding: isMobile ? '0.5rem 0.75rem' : '0.375rem 0.75rem',
                        borderRadius: '6px',
                        fontSize: isMobile ? '0.7rem' : '0.75rem',
                        fontWeight: '700',
                        boxShadow: `0 2px 8px ${daysColor}40`,
                        marginBottom: isMobile ? '0.75rem' : '0',
                        width: isMobile ? 'fit-content' : 'auto',
                        display: 'inline-block'
                      }}>
                        {isExpired ? `Expired ${Math.abs(daysToExpiry)} days ago` : `${daysToExpiry} Days`}
                      </div>
                    )}
                    
                    {/* Medicine Name */}
                    <div style={{
                      fontSize: isMobile ? '0.9rem' : '1rem',
                      fontWeight: '700',
                      color: '#1f2937',
                      marginBottom: '0.75rem',
                      paddingRight: isMobile ? '0' : (daysToExpiry !== null ? '120px' : '0'),
                      wordBreak: 'break-word'
                    }}>
                      {medicineName}
                    </div>
                    
                    {/* Details Grid */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                      gap: '0.75rem',
                      marginTop: '0.5rem'
                    }}>
                      {/* Composition */}
                      <div>
                        <div style={{
                          fontSize: '0.7rem',
                          color: '#6b7280',
                          fontWeight: '600',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          marginBottom: '0.25rem'
                        }}>
                          Composition
                        </div>
                        <div style={{
                          fontSize: '0.85rem',
                          color: '#374151',
                          fontWeight: '500'
                        }}>
                          {composition}
                        </div>
                      </div>
                      
                      {/* Batch No */}
                      <div>
                        <div style={{
                          fontSize: '0.7rem',
                          color: '#6b7280',
                          fontWeight: '600',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          marginBottom: '0.25rem'
                        }}>
                          Batch No
                        </div>
                        <div style={{
                          fontSize: '0.85rem',
                          color: '#374151',
                          fontWeight: '500'
                        }}>
                          {batchNo}
                        </div>
                      </div>
                      
                      {/* Expiry Date */}
                      <div>
                        <div style={{
                          fontSize: '0.7rem',
                          color: '#6b7280',
                          fontWeight: '600',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          marginBottom: '0.25rem'
                        }}>
                          Expiry Date
                        </div>
                        <div style={{
                          fontSize: '0.85rem',
                          color: '#374151',
                          fontWeight: '500'
                        }}>
                          {expiryDate ? formatDate(String(expiryDate)) : 'N/A'}
                        </div>
                      </div>
                      
                      {/* Days To Expiry */}
                      <div>
                        <div style={{
                          fontSize: '0.7rem',
                          color: '#6b7280',
                          fontWeight: '600',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          marginBottom: '0.25rem'
                        }}>
                          Days To Expiry
                        </div>
                        <div style={{
                          fontSize: '0.85rem',
                          color: daysColor,
                          fontWeight: '700'
                        }}>
                          {daysToExpiry !== null ? (
                            isExpired ? (
                              <span>Expired ({Math.abs(daysToExpiry)} days ago)</span>
                            ) : (
                              <span>{daysToExpiry} days</span>
                            )
                          ) : 'N/A'}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          
          {/* Summary */}
          {expiryAlerts.length > 0 && (
            <div style={{
              marginTop: '1.5rem',
              padding: isMobile ? '0.875rem' : '1rem',
              background: '#f9fafb',
              borderRadius: '8px',
              fontSize: isMobile ? '0.8rem' : '0.9rem',
              color: '#374151',
              textAlign: isMobile ? 'center' : 'left',
              width: '100%',
              boxSizing: 'border-box'
            }}>
              <strong>Total Alerts:</strong> {expiryAlerts.length} medicine(s) expiring within {daysThreshold} days
            </div>
          )}
        </>
      )}

      <style>{`
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        /* Custom scrollbar for expiry alerts */
        .expiry-alerts-scroll::-webkit-scrollbar {
          width: 6px;
        }

        .expiry-alerts-scroll::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 3px;
        }

        .expiry-alerts-scroll::-webkit-scrollbar-thumb {
          background: #ef4444;
          border-radius: 3px;
        }

        .expiry-alerts-scroll::-webkit-scrollbar-thumb:hover {
          background: #dc2626;
        }
      `}</style>
    </div>
  );
};

export default ExpiryAlerts;

